import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import FocusLock from 'react-focus-lock';
import useKeys from 'effects/keys';
import Exit from 'components/Icons/Exit/Exit';
import './Modal.scss';

const target = document.getElementById('modal');

/**
 * This is the Modal component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Modal({
  className,
  title,
  preContent,
  children,
  postContent,
  label,
  close,
  onClose,
}) {
  const node = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVisible(true);
    }, 120);

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    document.body.classList.add('popup-open');

    return function cleanup() {
      document.body.classList.remove('popup-open');
    };
  }, [visible]);

  useKeys('submenu', visible, {
    esc: () => {
      setVisible(false);
    },
  });

  function renderCloseButton() {
    if (!close) return null;

    return (
      <button
        autoFocus
        className="close"
        onClick={() => {
          setVisible(false);
        }}
      >
        <Exit title={label} />
      </button>
    );
  }

  function render() {
    return (
      <FocusLock>
        <div
          ref={node}
          className={classnames('modal', className, { visible })}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onTransitionEnd={({ target }) => {
            if (target === node.current) {
              if (!visible) {
                onClose();
              }
            }
          }}
          onClick={({ target }) => {
            if (target === node.current && close) {
              setVisible(false);
            }
          }}
        >
          <div className="box">
            {preContent}
            <div>
              {renderCloseButton()}
              <div className="content">{children}</div>
            </div>
            {postContent}
          </div>
        </div>
      </FocusLock>
    );
  }

  if (target) {
    return createPortal(render(), target);
  }
  return render();
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Modal.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  preContent: PropTypes.any,
  postContent: PropTypes.any,
  children: PropTypes.any,
  close: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Modal.defaultProps = {
  className: null,
  title: '',
  close: false,
  preContent: null,
  postContent: null,
  label: 'Exit',
  onClose: () => {},
};

export default Modal;
